import { twMerge } from 'tailwind-merge';

/** Styles to be applied to a `label`, has base styles but also those that apply based on the state of a preceding sibling [`peer`](https://tailwindcss.com/docs/hover-focus-and-other-states#styling-based-on-sibling-state). */
export const baseLabelStyles = twMerge(
  'pointer-events-none absolute ml-4 origin-left p-0 duration-100 ease-linear',
  // when the field has a value
  'translate-y-1 scale-[0.85]',
  // focused
  'peer-focus:text-brand',
  // when the field is focused and empty
  'peer-focus:peer-data-empty:translate-y-1 peer-focus:peer-data-empty:scale-[0.85]',
  // when the field is empty
  'peer-data-empty:translate-y-4 peer-data-empty:scale-100 peer-data-empty:text-on-surface-variant',
  // disabled
  'peer-disabled:text-on-surface/(--state-disabled-mix) peer-data-empty:peer-disabled:text-on-surface/(--state-disabled-mix)',
  // hovered
  'peer-hover:text-on-surface peer-data-empty:peer-hover:text-on-surface',
  // invalid
  'peer-data-invalid:text-critical peer-data-empty:peer-data-invalid:text-critical',
  // invalid hovered
  'peer-data-invalid:peer-hover:text-on-critical-container peer-data-empty:peer-data-invalid:peer-hover:text-on-critical-container',
);

export const basePickerLabelStyles = twMerge(
  'pointer-events-none absolute origin-left p-0 duration-100 ease-linear',
  // has value
  '-translate-y-5 scale-[0.85]',
  // focused
  'group-focus:text-brand group-data-empty:group-focus:text-brand',
  // field is empty and opened
  'group-data-[state="open"]:text-brand group-data-[state="open"]:group-data-empty:-translate-y-4 group-data-[state="open"]:group-data-empty:scale-[0.85]',
  // when the field is empty
  'group-data-empty:-translate-y-2 group-data-empty:scale-100 group-data-empty:text-on-surface-variant',
  // disabled
  'group-disabled:text-on-surface/(--state-disabled-mix) group-data-empty:group-disabled:text-on-surface/(--state-disabled-mix)',
  // hovered
  'group-hover:text-on-surface group-data-empty:group-hover:text-on-surface',
  // invalid
  'group-data-invalid:text-critical group-data-empty:group-data-invalid:text-critical',
  // invalid hovered
  'group-data-invalid:group-hover:text-on-critical-container group-data-empty:group-data-invalid:group-hover:text-on-critical-container',
);

/** Styles to be applied to a field control; i.e. `input` or `textarea` as a base. */
export const baseFieldStyles = twMerge(
  // enabled
  'w-full rounded-md border-none px-4 pt-5 pb-1 body-lg text-on-surface ring-1 ring-outline outline-none',
  // keeps the UA's `none` for form controls until a body case is set
  '[text-transform:var(--body-text-transform,none)]',
  // focused
  'focus:ring-3 focus:ring-brand',
  // hovered
  'hover:text-on-surface hover:ring-on-surface',
  // disabled
  'disabled:pointer-events-none disabled:text-on-surface/(--state-disabled-mix) disabled:ring-outline/(--state-disabled-mix)',
  // invalid,
  'data-invalid:ring-critical data-invalid:focus:ring-critical',
  // invalid hover
  'data-invalid:hover:ring-on-critical-container',
);

/** Styles for typed text fields, which are opaque, so the shadow can be a filter that never touches the ring. */
export const baseTextFieldStyles = twMerge(
  baseFieldStyles,
  'normal-case drop-shadow-(--field-shadow)',
);

export const basePickerFieldStyles = twMerge(
  baseFieldStyles,
  'group relative flex w-full items-center justify-between [&>span]:line-clamp-1',
  // maintain focus styles when open
  'data-[state="open"]:ring-3 data-[state="open"]:ring-brand',
  'data-invalid:ring-critical data-invalid:data-[state="open"]:ring-critical',
  // TODO: figure out how to have the trigger not collapse when there is no value
  // this is a hack to prevent the trigger from collapsing when there is no value
  'after:invisible after:content-["."]',
);

export const basePickerListboxStyles = twMerge(
  'relative z-50 min-w-32 translate-y-1 overflow-auto rounded-md bg-surface-container-low shadow-md',
  'max-h-(--radix-dropdown-menu-content-available-height) w-(--radix-dropdown-menu-trigger-width) max-w-(--radix-dropdown-menu-content-available-width) origin-(--radix-dropdown-menu-content-transform-origin)',
  'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
  'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
  'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
);

export const basePickerOptionStyles = twMerge(
  // base
  'flex w-full items-center justify-between gap-2 rounded-sm px-3 py-1.5 body-md outline-hidden select-none',
  // enabled
  'bg-surface-container-low text-on-surface',
  // highlighted - focus/hover
  'data-highlighted:bg-mix data-highlighted:mix-with-on-surface/(--state-focused-mix) data-highlighted:mix-surface-container-low',
);
